import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
interface WeighScaleVisualProps {
  leftWeight: number;
  rightWeight: number;
  leftLabel?: string;
  rightLabel?: string;
  unit?: 'g' | 'kg';
  label?: string;
  mode?: 'stimulus' | 'support';
  showQuestion?: boolean;
}
export function WeighScaleVisual({
  leftWeight,
  rightWeight,
  leftLabel,
  rightLabel,
  unit = 'kg',
  label,
  mode = 'support',
  showQuestion = false
}: WeighScaleVisualProps) {
  const computed = useMemo(() => {
    const difference = leftWeight - rightWeight;
    const maxTilt = 20; // degrees
    const tiltAngle = Math.max(-maxTilt, Math.min(maxTilt, difference / Math.max(leftWeight, rightWeight, 1) * maxTilt));
    const isBalanced = Math.abs(difference) < 0.01;
    return {
      difference,
      tiltAngle,
      isBalanced,
      heavier: difference > 0 ? 'left' : difference < 0 ? 'right' : 'balanced'
    };
  }, [leftWeight, rightWeight]);
  const scaleViz = <div className="flex justify-center items-center" data-id="element-9887">
      <svg width="280" height="160" viewBox="0 0 280 160" className="drop-shadow-sm" data-id="element-9888">
        {/* Base */}
        <rect x="110" y="140" width="60" height="15" rx="2" fill="#475569" stroke="#1e293b" strokeWidth="1" data-id="element-9889" />
        <rect x="120" y="130" width="40" height="10" fill="#64748b" data-id="element-9890" />

        {/* Center post */}
        <rect x="135" y="80" width="10" height="50" fill="#64748b" stroke="#475569" strokeWidth="1" data-id="element-9891" />

        {/* Balance beam (animated tilt) */}
        <motion.g initial={{
        rotate: 0
      }} animate={{
        rotate: computed.tiltAngle
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} style={{
        transformOrigin: '140px 80px'
      }} data-id="element-9892">
          {/* Beam */}
          <rect x="40" y="76" width="200" height="8" rx="4" fill="#f59e0b" stroke="#b45309" strokeWidth="1" data-id="element-9893" />

          {/* Center pivot */}
          <circle cx="140" cy="80" r="6" fill="#fbbf24" stroke="#b45309" strokeWidth="1" data-id="element-9894" />

          {/* Left chain */}
          <line x1="60" y1="80" x2="60" y2="120" stroke="#94a3b8" strokeWidth="2" data-id="element-9895" />

          {/* Right chain */}
          <line x1="220" y1="80" x2="220" y2="120" stroke="#94a3b8" strokeWidth="2" data-id="element-9896" />

          {/* Left plate */}
          <ellipse cx="60" cy="120" rx="30" ry="6" fill="#fde68a" stroke="#b45309" strokeWidth="1" data-id="element-9897" />
          <ellipse cx="60" cy="117" rx="30" ry="4" fill="#fef3c7" data-id="element-9898" />

          {/* Left weight label */}
          <text x="60" y="110" textAnchor="middle" fontSize="12" fill="#78350f" fontWeight="bold" data-id="element-9899">
            {showQuestion && !leftLabel ? '?' : leftLabel || `${leftWeight} ${unit}`}
          </text>

          {/* Right plate */}
          <ellipse cx="220" cy="120" rx="30" ry="6" fill="#fde68a" stroke="#b45309" strokeWidth="1" data-id="element-9900" />
          <ellipse cx="220" cy="117" rx="30" ry="4" fill="#fef3c7" data-id="element-9901" />

          {/* Right weight label */}
          <text x="220" y="110" textAnchor="middle" fontSize="12" fill="#78350f" fontWeight="bold" data-id="element-9902">
            {showQuestion && !rightLabel ? '?' : rightLabel || `${rightWeight} ${unit}`}
          </text>
        </motion.g>

        {/* Balance indicator */}
        {computed.isBalanced && mode === 'support' && <motion.g initial={{
        opacity: 0,
        scale: 0
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.6,
        duration: 0.4
      }} data-id="element-9903">
            <circle cx="140" cy="50" r="16" fill="#10b981" opacity="0.2" data-id="element-9904" />
            <text x="140" y="55" textAnchor="middle" fontSize="14" fill="#059669" fontWeight="bold" data-id="element-9905">
              ⚖️
            </text>
          </motion.g>}
      </svg>
    </div>;
  if (mode === 'stimulus') {
    return <div className="bg-white rounded-xl p-4 border border-slate-200 w-full max-w-sm mx-auto" data-id="element-9906">
        {label && <p className="text-sm font-semibold text-slate-700 mb-2 text-center" data-id="element-9907">
            {label}
          </p>}
        {scaleViz}
      </div>;
  }
  return <div className="bg-white rounded-xl border border-amber-100 overflow-hidden w-full max-w-sm mx-auto shadow-sm" data-id="element-9908">
      <div className="bg-amber-50 px-4 py-3 border-b border-amber-100" data-id="element-9909">
        <h3 className="text-sm font-bold text-amber-900 text-center" data-id="element-9910">
          {label || 'Weegschaal'}
        </h3>
      </div>

      <div className="p-4 bg-white" data-id="element-9911">{scaleViz}</div>

      <div className="bg-amber-50/50 p-4 border-t border-amber-50" data-id="element-9912">
        <p className="text-sm text-slate-700 text-center leading-relaxed" data-id="element-9913">
          {computed.isBalanced ? <>
              De weegschaal is <strong data-id="element-9914">in evenwicht</strong>. Beide kanten wegen
              even zwaar.
            </> : <>
              De{' '}
              <strong data-id="element-9915">
                {computed.heavier === 'left' ? 'linker' : 'rechter'}
              </strong>{' '}
              kant is zwaarder en zakt naar beneden.
            </>}
        </p>
      </div>
    </div>;
}